import { useEffect, useRef } from 'react';

export interface ReadinessRevisionInvalidationInput {
  /** Token naming the verified setup revision, or `null` while one is open. */
  verifiedRevision: string | null;
  /** Readiness is only worth invalidating while it is actually being computed. */
  enabled: boolean;
  /** Drops cached catalog evidence so requirements are measured again. */
  invalidate: () => void;
}

/**
 * Invalidates readiness catalogs whenever the verified setup revision changes.
 *
 * A connector mutation or a profile read-back settles a new revision, and the
 * catalogs sampled under the previous one describe a destination or binding
 * kind that may no longer apply. A verified revision already present at mount
 * also refreshes once: the cached catalog may predate the wizard mutation that
 * produced that revision and must not trigger a duplicate repair.
 */
export function useReadinessRevisionInvalidation({
  verifiedRevision,
  enabled,
  invalidate,
}: ReadinessRevisionInvalidationInput): boolean {
  const invalidateRef = useRef(invalidate);
  invalidateRef.current = invalidate;
  // `undefined` means "nothing observed yet"; `null` is a real revision state.
  const observedRevisionRef = useRef<string | null | undefined>(undefined);
  const refreshPending = enabled && (observedRevisionRef.current === undefined
    ? verifiedRevision !== null
    : observedRevisionRef.current !== verifiedRevision);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const observed = observedRevisionRef.current;
    observedRevisionRef.current = verifiedRevision;
    if (observed === undefined) {
      if (verifiedRevision !== null) {
        invalidateRef.current();
      }
      return;
    }
    if (observed === verifiedRevision) {
      return;
    }
    invalidateRef.current();
  }, [enabled, verifiedRevision]);

  return refreshPending;
}
